import { useState, useEffect, useCallback } from "react";
import type { VersionResponse, VersionCompareResponse } from "../../types/api";
import { versionAPI } from "../../lib/versionApi";
import styles from "./VersionHistorySidebar.module.css";

export interface VersionHistorySidebarProps {
  orgId: string;
  documentId: string;
  onRollback?: (document: unknown) => void;
  onClose?: () => void;
}

export function VersionHistorySidebar({
  orgId,
  documentId,
  onRollback,
  onClose,
}: VersionHistorySidebarProps) {
  const [versions, setVersions] = useState<VersionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVersions, setSelectedVersions] = useState<[string, string] | null>(null);
  const [comparison, setComparison] = useState<VersionCompareResponse | null>(null);
  const [comparingLoading, setComparingLoading] = useState(false);
  const [rollbackConfirm, setRollbackConfirm] = useState<string | null>(null);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const loadVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await versionAPI.getByDocumentId(orgId, documentId);
      setVersions(response.versions);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load versions");
    } finally {
      setLoading(false);
    }
  }, [orgId, documentId]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  // Reset search state when versions become empty (e.g. switching documents)
  useEffect(() => {
    if (versions.length === 0) {
      setSearchTerm("");
    }
  }, [versions.length]);

  const handleVersionSelect = useCallback(
    (versionId: string) => {
      if (!selectedVersions) {
        setSelectedVersions([versionId, versionId]);
      } else if (selectedVersions[0] === versionId) {
        setSelectedVersions(null);
        setComparison(null);
      } else {
        setSelectedVersions([selectedVersions[0], versionId]);
      }
    },
    [selectedVersions],
  );

  const handleCompare = useCallback(async () => {
    if (!selectedVersions || selectedVersions[0] === selectedVersions[1]) return;

    setComparingLoading(true);
    try {
      const result = await versionAPI.compare(
        orgId,
        documentId,
        selectedVersions[0],
        selectedVersions[1],
      );
      setComparison(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to compare versions");
    } finally {
      setComparingLoading(false);
    }
  }, [orgId, documentId, selectedVersions]);

  const handleRollback = useCallback(
    async (versionId: string) => {
      setRollbackLoading(true);
      try {
        const document = await versionAPI.rollback(orgId, documentId, versionId);
        onRollback?.(document);
        setRollbackConfirm(null);
        loadVersions();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to rollback");
      } finally {
        setRollbackLoading(false);
      }
    },
    [orgId, documentId, onRollback, loadVersions],
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const filteredVersions = versions.filter((v) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      v.change_summary?.toLowerCase().includes(searchLower) ||
      formatDate(v.created_at).toLowerCase().includes(searchLower) ||
      v.initiator.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Version History</h3>
        {onClose && (
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        )}
      </div>

      {versions.length > 0 && (
        <div className={styles.search}>
          <SearchIcon />
          <input
            type="text"
            placeholder="Search versions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>
          <div className={styles.spinner} />
        </div>
      ) : (
        <>
          <div className={styles.versionList}>
            {filteredVersions.length === 0 ? (
              <div className={styles.empty}>
                {searchTerm ? "No matching versions" : "No versions yet"}
              </div>
            ) : (
              filteredVersions.map((version, index) => (
                <div
                  key={version.id}
                  className={`${styles.versionItem} ${
                    selectedVersions?.includes(version.id) ? styles.versionItemSelected : ""
                  }`}
                  onClick={() => handleVersionSelect(version.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleVersionSelect(version.id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className={styles.versionHeader}>
                    <span className={styles.versionNumber}>v{versions.length - index}</span>
                    <span
                      className={`${styles.initiatorBadge} ${styles[`initiator${version.initiator}`]}`}
                    >
                      {version.initiator === "ai" ? "AI" : version.initiator}
                    </span>
                  </div>
                  <div className={styles.versionMeta}>
                    <span className={styles.versionTime}>
                      {formatRelativeTime(version.created_at)}
                    </span>
                    <span className={styles.versionWords}>
                      {version.word_count} {version.word_count === 1 ? "word" : "words"}
                    </span>
                  </div>
                  {version.change_summary && (
                    <p className={styles.versionSummary}>{version.change_summary}</p>
                  )}
                  {rollbackConfirm === version.id ? (
                    <div className={styles.rollbackConfirm}>
                      <span>Restore this version?</span>
                      <div className={styles.rollbackActions}>
                        <button
                          type="button"
                          className={styles.confirmButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRollback(version.id);
                          }}
                          disabled={rollbackLoading}
                        >
                          {rollbackLoading ? "..." : "Restore"}
                        </button>
                        <button
                          type="button"
                          className={styles.cancelButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            setRollbackConfirm(null);
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className={styles.rollbackButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        setRollbackConfirm(version.id);
                      }}
                    >
                      <RollbackIcon />
                      Restore
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {selectedVersions && selectedVersions[0] !== selectedVersions[1] && (
            <div className={styles.compareSection}>
              <button
                type="button"
                className={styles.compareButton}
                onClick={handleCompare}
                disabled={comparingLoading}
              >
                {comparingLoading ? "Comparing..." : "Compare Selected"}
              </button>
            </div>
          )}

          {comparison && (
            <div className={styles.diffView}>
              <div className={styles.diffHeader}>
                <h4>Changes</h4>
                <div className={styles.diffSummary}>
                  <span className={styles.added}>+{comparison.diff.summary.lines_added}</span>
                  <span className={styles.removed}>-{comparison.diff.summary.lines_removed}</span>
                </div>
              </div>
              <div className={styles.diffContent}>
                {comparison.diff.changes.map((change, idx) => (
                  <div key={idx} className={`${styles.diffLine} ${styles[`diff${change.type}`]}`}>
                    {change.type === "added" && <span className={styles.diffPrefix}>+</span>}
                    {change.type === "removed" && <span className={styles.diffPrefix}>-</span>}
                    {change.type === "unchanged" && <span className={styles.diffPrefix}> </span>}
                    <span className={styles.diffText}>{change.content}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M12 4L4 12M4 4L12 12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9.5 9.5L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function RollbackIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path
        d="M2 5H7C8.657 5 10 6.343 10 8C10 9.657 8.657 11 7 11H5M2 5L4 3M2 5L4 7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
